import { useState, useEffect, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { Calculator, Clock, TrendingUp, Users, ChevronDown, ChevronUp } from "lucide-react";

export function ROICalculator() {
  const [teamSize, setTeamSize] = useState(5);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Advanced assumptions (collapsed by default)
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Advanced overrides (optional)
  const [sprintLengthWeeks, setSprintLengthWeeks] = useState(2); // default 2 weeks
  const [reqsPerSprintOverride, setReqsPerSprintOverride] = useState<number | null>(null);


  // Defaults you asked for
  const [engineersPerPM, setEngineersPerPM] = useState(10);     // default: 10 engineers per PM
  const [engineersInvolvedPerClarification, setEngineersInvolvedPerClarification] = useState(
      Math.max(1, Math.round(10 * 0.5))
    ); // default: 50% of engineers get pulled into a clarification loop
  const [meetingMinsPerClarification, setMeetingMinsPerClarification] = useState(15); // meeting / back-and-forth time
  const [reworkHrsPerClarification, setReworkHrsPerClarification] = useState(2);      // rework due to ambiguity
  const [qaMultiplier, setQaMultiplier] = useState(1.2);        // conservative downstream multiplier

  const [engInvolvedTouched, setEngInvolvedTouched] = useState(false);

  useEffect(() => {
  if (engInvolvedTouched) {
    // If user changed involved manually, just clamp to new max
    setEngineersInvolvedPerClarification((prev) =>
      Math.min(prev, engineersPerPM)
    );
    return;
  }

  // Default behavior: always keep it at 50% of engineersPerPM
  setEngineersInvolvedPerClarification(
    Math.max(1, Math.round(engineersPerPM * 0.5))
  );
}, [engineersPerPM, engInvolvedTouched]);

  // Hourly rates (keep PM=75 as you had)
  const PM_HOURLY = 75;
  const ENG_HOURLY = 90;

  // ── Base (keep original feel) ───────────────────────────────────────────────
  const SPRINTS_PER_YEAR = Math.floor(52 / sprintLengthWeeks);

  // Existing PM-centric metrics (unchanged)
  const hoursPerSprintSaved = Math.round(teamSize * 1.5);

  // Better: keep your old feel by mapping from PM count to a per-PM default:
  const reqsPerPMDefault = 3; // choose 3–5; 3 keeps conservative
  const reqsPerPMPerSprintFinal = reqsPerSprintOverride ?? reqsPerPMDefault;

  // Requirements per PM per sprint (override). If not set, infer from your old heuristic.
  const reqsPerPMPerSprint =
    reqsPerSprintOverride ?? reqsPerPMDefault// fallback if needed

  // Org-wide derived
  const reqsPerSprint = reqsPerPMPerSprintFinal * teamSize;

  // default mapping (tweak if you want)

  const requirementsPerMonth = Math.round((reqsPerSprint * SPRINTS_PER_YEAR) / 12);

  // Clarifications avoided per PM per sprint
  const clarificationsAvoidedPerPMSprint =
    reqsPerSprintOverride == null
      ? 3.2 // keep your old heuristic but per PM
      : reqsPerPMPerSprintFinal * 1.2; // 1.2 clarifications avoided per req (per PM)

  // Org-wide per sprint (what you display on the card can still be per PM if you want)
  const clarifyingQuestionsAvoided = Math.round(clarificationsAvoidedPerPMSprint * teamSize);


  const annualHoursSaved = hoursPerSprintSaved * SPRINTS_PER_YEAR;

  // ── New: Team structure derived from PM count ───────────────────────────────
  // const pmCount = teamSize; // interpret the slider as number of PMs / product writers

  // Clarification events avoided (your existing metric is the base unit)
  const clarificationsAvoidedPerYear = clarifyingQuestionsAvoided * SPRINTS_PER_YEAR;

  // How many engineers are actually pulled into a clarification loop (cap by total engineers)
  const engInvolved = Math.min(engineersPerPM, engineersInvolvedPerClarification);
  const effectiveEngInvolved =
    1 + (engInvolved - 1) * 0.35;


  // ── Time saved model ────────────────────────────────────────────────────────
  // PM time saved:
  // - your original annualHoursSaved captures PM async time saved per sprint
  // - add meeting / clarification time saved
  const pmMeetingHrsSavedRaw = (clarificationsAvoidedPerYear * meetingMinsPerClarification) / 60;
  const pmMeetingHrsSaved = Math.min(pmMeetingHrsSavedRaw, annualHoursSaved);
  const pmTotalHrsSaved = annualHoursSaved + pmMeetingHrsSaved;

  // Engineering time saved:
  // - meeting time saved (each clarification loop avoided saves time for each involved engineer)
  // - rework time saved (rework hours avoided per involved engineer)
  const engMeetingHrsSaved = (clarificationsAvoidedPerYear * meetingMinsPerClarification * effectiveEngInvolved) / 60;
  const engReworkHrsSaved = clarificationsAvoidedPerYear * reworkHrsPerClarification * effectiveEngInvolved;
  const engTotalHrsSaved = engMeetingHrsSaved + engReworkHrsSaved;

  // QA time saved (convert qa multiplier into equivalent hours, proportional to PM+Eng hours)
  // This keeps "hrs saved" consistent with "$ saved" without inventing a QA hourly rate.
  const qaHrsSaved = (pmTotalHrsSaved + engTotalHrsSaved) * (qaMultiplier - 1);

  const totalAnnualHrsSaved = pmTotalHrsSaved + engTotalHrsSaved + qaHrsSaved;
  const totalHrsPerSprintSaved = totalAnnualHrsSaved / SPRINTS_PER_YEAR;

  // ── $ value model ───────────────────────────────────────────────────────────
  const pmValueSaved = pmTotalHrsSaved * PM_HOURLY;
  const engValueSaved = engTotalHrsSaved * ENG_HOURLY;

  // QA/downstream churn as a conservative multiplier on (PM+Eng) value
  const qaValueSaved = (pmValueSaved + engValueSaved) * (qaMultiplier - 1);

  const totalValueSaved = Math.round(pmValueSaved + engValueSaved + qaValueSaved);


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-32 bg-card/50">
      <div className="container px-4 md:px-6">
        <div
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Calculator className="w-4 h-4" />
              ROI Calculator
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              See your potential savings
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Adjust your team size to see how much time Reqqy can save your
              product team.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Team Size Slider */}
            <div className="glass rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="font-medium">Product Team Size</span>
                </div>
                <div className="text-3xl font-display font-bold text-primary">
                  {teamSize} <span className="text-lg text-muted-foreground">people</span>
                </div>
              </div>
              <Slider
                value={[teamSize]}
                onValueChange={(value) => setTeamSize(value[0])}
                min={1}
                max={50}
                step={1}
                className="w-full"
                aria-label="Product team size"
              />
              <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                <span>1 person</span>
                <span>50 people</span>
              </div>
            </div>

            {/* Additional assumptions (collapsed) */}
            <div className="glass rounded-2xl overflow-hidden mb-8">
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="w-full flex items-center justify-between px-8 py-5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={advancedOpen}
              >
                <span>Additional assumptions (optional)</span>
                {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {advancedOpen && (
                <div className="px-8 pb-8 border-t border-border/50 space-y-8">
                  <p className="text-xs text-muted-foreground pt-4">
                    Defaults are conservative. Reqqy improves clarity upstream, which reduces meetings, rework, and QA churn downstream.
                    <br />
                    These assumptions are <span className="text-foreground font-medium">per PM</span>. Totals are calculated across {teamSize} PMs.
                  </p>


                  <AssumptionSlider
                    label="Engineers per PM"
                    value={engineersPerPM}
                    onChange={setEngineersPerPM}
                    min={3}
                    max={20}
                    step={1}
                    unit=""
                  />

                  <AssumptionSlider
                    label="Engineers involved per clarification loop"
                    value={engineersInvolvedPerClarification}
                    onChange={(v) => {
                      setEngInvolvedTouched(true);
                      setEngineersInvolvedPerClarification(v);
                    }}
                    min={1}
                    max={engineersPerPM}
                    step={1}
                    unit=""
                  />


                  <AssumptionSlider
                    label="Meeting / back-and-forth time per clarification"
                    value={meetingMinsPerClarification}
                    onChange={setMeetingMinsPerClarification}
                    min={5}
                    max={60}
                    step={5}
                    unit="mins"
                  />

                  <AssumptionSlider
                    label="Engineering rework per clarification"
                    value={reworkHrsPerClarification}
                    onChange={setReworkHrsPerClarification}
                    min={0}
                    max={8}
                    step={1}
                    unit="hrs"
                  />
                  <AssumptionSlider
                    label="Requirements reviewed per PM per sprint (override)"
                    value={reqsPerSprintOverride ?? reqsPerSprint/teamSize}
                    onChange={(v) => setReqsPerSprintOverride(v)}
                    min={3}
                    max={20}
                    step={1}
                    unit="reqs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Total requirements per sprint = {reqsPerPMPerSprint} × {teamSize} PMs
                  </p>

                  <button
                    type="button"
                    onClick={() => setReqsPerSprintOverride(null)}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                  >
                    Reset to auto-estimate
                  </button>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-sm">Sprint length</span>
                      <span className="text-2xl font-display font-bold text-primary">
                        {sprintLengthWeeks} <span className="text-base text-muted-foreground">weeks</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-3">
                      {[1, 2, 3, 4].map((w) => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => setSprintLengthWeeks(w)}
                          className={`rounded-xl py-3 text-sm font-medium transition-colors ${
                            sprintLengthWeeks === w
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted/60 text-muted-foreground hover:text-foreground"
                          }`}
                          aria-pressed={sprintLengthWeeks === w}
                        >
                          {w}w
                        </button>
                      ))}
                    </div>
                  </div>


                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-sm">QA & downstream multiplier</span>
                      <span className="text-2xl font-display font-bold text-primary">
                        ×{qaMultiplier.toFixed(1)}
                      </span>
                    </div>
                    <Slider
                      value={[Math.round(qaMultiplier * 10)]}
                      onValueChange={(v) => setQaMultiplier(v[0] / 10)}
                      min={10}
                      max={20}
                      step={1}
                      className="w-full"
                      aria-label="QA multiplier"
                    />
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                      <span>×1.0 (none)</span>
                      <span>×2.0 (high churn)</span>
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* Results Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ResultCard
                icon={Clock}
                value={Math.round(totalHrsPerSprintSaved)}
                unit="hrs"
                label="Saved per sprint"
                isVisible={isVisible}
                delay={100}
              />
              <ResultCard
                icon={TrendingUp}
                value={Math.round(totalAnnualHrsSaved)}
                unit="hrs"
                label="Saved annually"
                isVisible={isVisible}
                delay={200}
                highlight
              />
              <ResultCard
                icon={Calculator}
                value={requirementsPerMonth}
                unit=""
                label="Requirements reviewed/mo"
                isVisible={isVisible}
                delay={300}
              />
              <ResultCard
                icon={Users}
                value={clarifyingQuestionsAvoided}
                unit=""
                label="Questions avoided/sprint"
                isVisible={isVisible}
                delay={400}
              />
            </div>

            {/* ROI Summary */}
            <div
              className={`mt-8 p-6 rounded-2xl gradient-primary text-center transition-all duration-700 delay-500 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <p className="text-primary-foreground/80 mb-2">
                Based on conservative rates across PM, Engineering, and QA, your team could save
              </p>

              <p className="text-4xl font-display font-bold text-primary-foreground">
                ${totalValueSaved.toLocaleString()}
                <span className="text-lg font-normal text-primary-foreground/80">
                  {" "}
                  per year
                </span>
              </p>
              <p className="text-primary-foreground/70 text-sm mt-2">
                Includes downstream Engineering rework + QA churn avoided (editable assumptions).
              </p>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ResultCardProps {
  icon: React.ElementType;
  value: number;
  unit: string;
  label: string;
  isVisible: boolean;
  delay: number;
  highlight?: boolean;
}

function ResultCard({
  icon: Icon,
  value,
  unit,
  label,
  isVisible,
  delay,
  highlight,
}: ResultCardProps) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 1000;
    const steps = 30;
    const stepValue = value / steps;
    let current = 0;

    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        current += stepValue;
        if (current >= value) {
          setDisplayValue(value);
          clearInterval(interval);
        } else {
          setDisplayValue(Math.round(current));
        }
      }, duration / steps);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, value, delay]);

  return (
    <div
      className={`p-6 rounded-2xl transition-all duration-500 ${
        highlight
          ? "glass border-primary/30 glow-soft"
          : "glass"
      } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <Icon
        className={`w-5 h-5 mb-3 ${highlight ? "text-primary" : "text-muted-foreground"}`}
      />
      <div className="text-3xl font-display font-bold mb-1">
        {displayValue}
        {unit && <span className="text-lg text-muted-foreground">{unit}</span>}
      </div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function AssumptionSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-medium text-sm">{label}</span>
        <span className="text-2xl font-display font-bold text-primary">
          {value} {unit && <span className="text-base text-muted-foreground">{unit}</span>}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
        className="w-full"
        aria-label={label}
      />
      <div className="flex justify-between mt-2 text-xs text-muted-foreground">
        <span>{min}{unit ? ` ${unit}` : ""}</span>
        <span>{max}{unit ? ` ${unit}` : ""}</span>
      </div>
    </div>
  );
}

